import { useCallback, useState } from 'react';
import { prop } from 'ramda';
import { debounce } from 'underscore';
import { useDeviceSelector } from 'js/store/hooks';
import ResizeObserver from 'resize-observer-polyfill';

export const SCREEN_SIZES = {
    narrow: 'narrow',
    middle: 'middle',
    wide: 'wide'
};

export const getScreenSize = (container, sizeLimits) => {
    const { offsetWidth } = container;
    const { middle, narrow } = sizeLimits;

    if (offsetWidth <= narrow) {
        return SCREEN_SIZES.narrow;
    }

    if (offsetWidth <= middle) {
        return SCREEN_SIZES.middle;
    }

    return SCREEN_SIZES.wide;
};

export const debouncedGetScreenSize = debounce(getScreenSize, 300);

// TODO - use common/hooks/useResizeObserver
export const useResizeObserverLegacy = ({ setScreenSize, sizeLimits }) => {
    const [resizeObserver] = useState(new ResizeObserver((entries) => {
        setScreenSize(getScreenSize(entries[0].target, sizeLimits));
    }));

    const callbackRef = useCallback((node) => {
        if (node) {
            resizeObserver.observe(node);
        } else {
            resizeObserver.disconnect();
        }
    }, [resizeObserver]);

    return {
        callbackRef
    };
};

export const useOrientationChange = ({ setScreenSize, sizeLimits }) => {
    const orientation = useDeviceSelector(prop('orientation'));
    const callbackRef = useCallback(
        (node) => {
            if (node) {
                setScreenSize(debouncedGetScreenSize(node, sizeLimits));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [orientation]
    );

    return {
        callbackRef
    };
};

export const useScreenSize = (sizeLimits, useCallbackRef) => {
    const [screenSize, setScreenSize] = useState(SCREEN_SIZES.wide);
    const { callbackRef } = useCallbackRef({ screenSize, setScreenSize, sizeLimits });

    return {
        callbackRef,
        screenSize
    };
};

export default ({ middle = 800, narrow = 600 } = {}) => {
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const useCallbackRef = isDesktop ? useResizeObserverLegacy : useOrientationChange;

    return useScreenSize({ middle, narrow }, useCallbackRef);
};
